import { useEffect, useState } from "react"
import { AdminTournamentClient } from "@/domain/admin/client/AdminTournamentClient"
import type {
  TournamentFormErrors,
  TournamentFormInput,
} from "@/domain/tournament/model/Tournament"

function makeInitFormInput(): TournamentFormInput {
  return {
    id: "",
    name: "",
    venue: "",
    startAt: "",
    endAt: "",
    entryOpenAt: "",
    entryCloseAt: "",
    status: "TOURNAMENT_SCHEDULED",
    notice: "",
    sports: [],
  }
}

/**
 * 대회 등록 · 수정 폼 훅.
 *
 * 등록과 수정이 같은 폼을 쓰고 id 가 있으면 수정이다 — 서버도 같은 `save` 하나를 쓴다.
 * 기간의 앞뒤 관계는 서버도 검증하지만, 저장을 누르기 전에 알려주는 편이 낫다.
 */
export function useAdminTournamentForm(tournamentId: string) {
  const [input, setInput] = useState<TournamentFormInput>(makeInitFormInput)
  const [errors, setErrors] = useState<TournamentFormErrors>({})
  const [errorMessage, setErrorMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [savedId, setSavedId] = useState("")

  const isModifyMode = Boolean(tournamentId)

  function setField(field: keyof TournamentFormInput, value: string) {
    setInput((current) => ({ ...current, [field]: value }))
  }

  function toggleSport(sport: string) {
    setInput((current) => {
      const isSelected = current.sports.includes(sport)
      const sports = isSelected
        ? current.sports.filter((selected) => selected !== sport)
        : [...current.sports, sport]

      return { ...current, sports }
    })
  }

  function validate(): string {
    const nextErrors: TournamentFormErrors = {}

    if (!input.name.trim()) nextErrors.name = "대회명을 입력해 주세요."
    if (!input.startAt) nextErrors.startAt = "대회 시작일시를 입력해 주세요."
    if (!input.endAt) nextErrors.endAt = "대회 종료일시를 입력해 주세요."
    if (!input.entryOpenAt) {
      nextErrors.entryOpenAt = "접수 시작일시를 입력해 주세요."
    }
    if (!input.entryCloseAt) {
      nextErrors.entryCloseAt = "접수 마감일시를 입력해 주세요."
    }
    if (input.sports.length === 0) {
      nextErrors.sports = "개최 종목을 1개 이상 선택해 주세요."
    }

    const hasPeriod = input.startAt && input.endAt
    if (hasPeriod && input.startAt > input.endAt) {
      nextErrors.endAt = "대회 종료일시가 시작일시보다 빠릅니다."
    }

    const hasEntryPeriod = input.entryOpenAt && input.entryCloseAt
    if (hasEntryPeriod && input.entryOpenAt > input.entryCloseAt) {
      nextErrors.entryCloseAt = "접수 마감일시가 시작일시보다 빠릅니다."
    }
    if (
      input.entryCloseAt &&
      input.startAt &&
      input.entryCloseAt > input.startAt
    ) {
      nextErrors.entryCloseAt = "접수 마감은 대회 시작 전이어야 합니다."
    }

    setErrors(nextErrors)

    return Object.keys(nextErrors).length > 0 ? "입력 값을 확인해 주세요." : ""
  }

  async function load() {
    if (!isModifyMode) return

    setIsLoading(true)

    try {
      const loaded = await AdminTournamentClient.getFormInput(tournamentId)
      setInput(loaded)
      setErrorMessage("")
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "대회 정보를 불러오지 못했습니다.",
      )
    } finally {
      setIsLoading(false)
    }
  }

  async function submit() {
    const validationMessage = validate()

    if (validationMessage) {
      setErrorMessage(validationMessage)
      return
    }

    setIsLoading(true)

    try {
      const id = await AdminTournamentClient.save(input)
      setSavedId(id)
      setErrorMessage("")
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "대회 저장에 실패했습니다.",
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setInput(makeInitFormInput())
    setErrors({})
    setSavedId("")
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId])

  return {
    input,
    errors,
    errorMessage,
    isLoading,
    isModifyMode,
    savedId,
    setField,
    toggleSport,
    submit,
  }
}
